import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  DirectionsCar as CarIcon,
  LocalLibrary as LibraryIcon,
  SportsKabaddi as SportsIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  CurrencyRupee,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import AuthService from '../../Auth/AuthService';
import { useRef } from 'react';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  padding: '0 8px',
  height: 24,
  ...(status?.toLowerCase() === 'paid' && {
    backgroundColor: '#4caf50',
    color: 'white',
    '& .MuiChip-icon': { color: 'white' }
  }),
  ...(status?.toLowerCase() === 'partial' && {
    backgroundColor: '#ff9800',
    color: 'white',
    '& .MuiChip-icon': { color: 'white' }
  }),
  ...(status?.toLowerCase() === 'unpaid' && {
    backgroundColor: '#f44336',
    color: 'white',
    '& .MuiChip-icon': { color: 'white' }
  })
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
  }
}));

const ReceiptContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: '#ffffff',
  maxWidth: '800px',
  margin: '0 auto',
  '& .receipt-header': {
    textAlign: 'center',
    borderBottom: '2px solid #1976d2',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  '& .receipt-title': {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1976d2',
    marginBottom: theme.spacing(0.5)
  },
  '& .receipt-subtitle': {
    fontSize: '14px',
    color: '#666',
    marginBottom: theme.spacing(2)
  },
  '& .receipt-details': {
    marginBottom: theme.spacing(3)
  },
  '& .receipt-row': {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: '1px solid #f0f0f0'
  },
  '& .receipt-total': {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 0),
    borderTop: '2px solid #1976d2',
    marginTop: theme.spacing(1),
    fontWeight: 700
  },
  '& .receipt-footer': {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center',
    color: '#666',
    fontSize: '12px'
  }
}));

const apiService = {
  // Fetch all fee records
  getFeeRecords: async (studentId) => {
    try {
      const response = await AuthService.getAuth(`/fees/student/${studentId}`);
      return response;
    } catch (error) {
      console.error('Error fetching fee records:', error);
      throw error;
    }
  },

  // Get single fee record by ID
  getFeeRecordById: async (id) => {
    try {
      const response = await AuthService.getAuth(`/fees/fees-details/${id}`);
      return response;
    } catch (error) {
      console.error('Error fetching fee record:', error);
      throw error;
    }
  },

  // Update fee payment status
  updateFeePayment: async (feeEntryId, paymentData) => {
    try {
      const response = await AuthService.putAuth(`/fees/entry/${feeEntryId}`, paymentData);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating fee payment:', error);
      throw error;
    }
  },

  // Export fee report
  exportReport: async () => {
    try {
      const response = await AuthService.getAuth(`/fees/export`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fee-report-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }
};

// Helper function to group fees by feeType
const groupFeesByType = (feeRecords) => {
  if (!Array.isArray(feeRecords) || feeRecords.length === 0) {
    return {};
  }

  const grouped = {};

  feeRecords.forEach(record => {
    if (record?.fees && Array.isArray(record.fees)) {
      record.fees.forEach(fee => {
        const feeType = fee?.feeType || 'Other';
        if (!grouped[feeType]) {
          grouped[feeType] = {
            feeType: feeType,
            items: [],
            totalAmount: 0,
            totalPaid: 0,
            status: 'Unpaid'
          };
        }
        grouped[feeType].items.push({
          ...fee,
          parentRecord: record
        });
        grouped[feeType].totalAmount += (fee?.amount || 0);
        grouped[feeType].totalPaid += (fee?.feesEntry?.paidAmount || 0);

        // Update status based on payment
        if (fee?.feesEntry?.status) {
          const status = fee.feesEntry.status;
          if (status === 'Paid') {
            grouped[feeType].status = 'Paid';
          } else if (status === 'Partial' && grouped[feeType].status !== 'Paid') {
            grouped[feeType].status = 'Partial';
          }
        }
      });
    }
  });

  return grouped;
};

// Helper function to generate receipt HTML
const generateReceiptHTML = (fee, studentInfo) => {
  const feeType = fee?.feeType || 'N/A';
  const amount = fee?.amount || 0;
  const paidAmount = fee?.feesEntry?.paidAmount || 0;
  const status = fee?.feesEntry?.status || 'N/A';
  const dueDate = fee?.feesEntry?.dueDate ? new Date(fee.feesEntry.dueDate).toLocaleDateString('en-IN') : 'N/A';
  const paymentDate = fee?.feesEntry?.createdAt ? new Date(fee.feesEntry.createdAt).toLocaleDateString('en-IN') : 'N/A';
  const paymentMode = fee?.feesEntry?.paymentMode || 'N/A';
  const createdBy = fee?.feesEntry?.createdBy || 'System';
  const studentName = studentInfo?.studentName || 'Student';
  const className = studentInfo?.className || 'N/A';

  return `
    <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto;">
      <div style="text-align: center; border-bottom: 2px solid #1976d2; padding-bottom: 20px; margin-bottom: 30px;">
        <h2 style="color: #1976d2; margin: 0;">School Fee Receipt</h2>
        <p style="color: #666; margin: 5px 0;">Official Payment Receipt</p>
        <p style="color: #999; font-size: 12px;">Receipt #: ${new Date().getTime().toString().slice(-8)}</p>
      </div>

      <div style="margin-bottom: 20px;">
        <h3 style="color: #333; margin-bottom: 10px;">Student Information</h3>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Student Name:</span>
          <span style="font-weight: 600;">${studentName}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Class:</span>
          <span style="font-weight: 600;">${className}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Student ID:</span>
          <span style="font-weight: 600;">${studentInfo?.studentId || 'N/A'}</span>
        </div>
      </div>

      <div style="margin-bottom: 20px;">
        <h3 style="color: #333; margin-bottom: 10px;">Fee Details</h3>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Fee Type:</span>
          <span style="font-weight: 600; text-transform: capitalize;">${feeType}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Total Amount:</span>
          <span style="font-weight: 600;">₹${amount.toLocaleString('en-IN')}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Paid Amount:</span>
          <span style="font-weight: 600; color: ${status === 'Paid' ? '#4caf50' : '#ff9800'};">₹${paidAmount.toLocaleString('en-IN')}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Payment Mode:</span>
          <span style="font-weight: 600; text-transform: uppercase;">${paymentMode}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Payment Date:</span>
          <span style="font-weight: 600;">${paymentDate}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Due Date:</span>
          <span style="font-weight: 600;">${dueDate}</span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Status:</span>
          <span style="font-weight: 600; color: ${status === 'Paid' ? '#4caf50' : status === 'Partial' ? '#ff9800' : '#f44336'};">
            ${status}
          </span>
        </div>
        <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f0f0f0;">
          <span style="color: #666;">Created By:</span>
          <span style="font-weight: 600;">${createdBy}</span>
        </div>
      </div>

      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #1976d2; text-align: center; color: #666; font-size: 12px;">
        <p style="margin: 5px 0;">This is a computer-generated receipt and does not require a physical signature.</p>
        <p style="margin: 5px 0;">For any queries, please contact the accounts department.</p>
        <p style="margin: 5px 0; color: #999;">Generated on: ${new Date().toLocaleString('en-IN')}</p>
      </div>
    </div>
  `;
};

// Main Component
const FeeView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const location = useLocation();
  const { classRoom } = location.state || {};

  const [feeRecords, setFeeRecords] = useState([]);
  const [groupedFees, setGroupedFees] = useState({});
  const [loading, setLoading] = useState(!classRoom);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [refreshing, setRefreshing] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const hasFetched = useRef(false);

  // Load fee data
  const loadFeeData = async () => {
    try {
      setLoading(true);
      setError(null);

      let data;
      if (classRoom?.studentId) {
        const url = AuthService.pathUrl("GET", `/fees/student/${classRoom?.studentId}`);
        data = await AuthService.getAuth(url);
        setFeeRecords(data);

        // Group fees by type
        const grouped = groupFeesByType(data);
        setGroupedFees(grouped);

        // Auto-expand all groups initially
        const expandedState = {};
        Object.keys(grouped).forEach(key => {
          expandedState[key] = true;
        });
        setExpandedGroups(expandedState);
      }
    } catch (err) {
      setError(err.message || 'Failed to load fee records');
      setSnackbar({
        open: true,
        message: 'Failed to load fee records. Please try again.',
        severity: 'error'
      });
      setFeeRecords([]);
      setGroupedFees({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    setRefreshing(true);
    loadFeeData();
  };

  // Initial load
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (classRoom) {
      loadFeeData();
    } else {
      setLoading(false);
    }
  }, []);

  // Toggle group expansion
  const toggleGroup = (feeType) => {
    setExpandedGroups(prev => ({
      ...prev,
      [feeType]: !prev[feeType]
    }));
  };

  // Generate and download receipt for a specific fee
  const handleDownloadReceipt = async (fee) => {
    try {
      // Get student information from parent record
      const studentInfo = {
        studentName: classRoom?.studentName || 'Student',
        className: classRoom?.standard || 'N/A',
        studentId: classRoom?.studentId || 'N/A'
      };

      // Generate receipt HTML
      const receiptHTML = generateReceiptHTML(fee, studentInfo);
      
      // Create a temporary container for the receipt
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = receiptHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      document.body.appendChild(tempContainer);

      // Generate PDF
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = 210;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`fee-receipt-${fee?.id || 'unknown'}.pdf`);

      setSnackbar({
        open: true,
        message: 'Receipt downloaded successfully!',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error generating receipt:', err);
      setSnackbar({
        open: true,
        message: 'Failed to generate receipt. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle export report
  const handleExportReport = async () => {
    try {
      await apiService.exportReport();
      setSnackbar({
        open: true,
        message: 'Report exported successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to export report. Please try again.',
        severity: 'error'
      });
    }
  };

  // Calculate totals
  const totalAnnualFees = Array.isArray(feeRecords)
    ? feeRecords.reduce((sum, record) => (record?.vehicleFees || 0) + (record?.annualFees || 0), 0)
    : 0;

  const totalSchoolFees = Array.isArray(feeRecords)
    ? feeRecords.reduce((sum, record) => (record?.annualFees || 0), 0)
    : 0;

  const totalVehicleFees = Array.isArray(feeRecords)
    ? feeRecords.reduce((sum, record) => (record?.vehicleFees || 0), 0)
    : 0;

  const totalPaid = Array.isArray(feeRecords)
    ? feeRecords.reduce((sum, record) => sum + (record?.paidAmount || 0), 0)
    : 0;

  const totalRemaining = totalAnnualFees - totalPaid;
  const overallProgress = totalAnnualFees > 0 ? (totalPaid / totalAnnualFees) * 100 : 0;

  // Get fee type icon
  const getFeeTypeIcon = (feeType) => {
    const type = feeType?.toLowerCase() || '';
    if (type.includes('admission') || type.includes('tuition')) return <SchoolIcon color="primary" />;
    if (type.includes('vehicle') || type.includes('bus') || type.includes('transport')) return <CarIcon color="secondary" />;
    if (type.includes('library')) return <LibraryIcon color="info" />;
    if (type.includes('sport')) return <SportsIcon color="success" />;
    return <ReceiptIcon color="action" />;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'paid') return <CheckCircleIcon sx={{ fontSize: 16 }} />;
    if (statusLower === 'partial') return <PendingIcon sx={{ fontSize: 16 }} />;
    return <WarningIcon sx={{ fontSize: 16 }} />;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return '₹0';
    return `₹${amount.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Get status summary
  const getStatusSummary = () => {
    const statusCount = {
      Paid: 0,
      Partial: 0,
      Unpaid: 0
    };

    if (Array.isArray(feeRecords)) {
      feeRecords.forEach(record => {
        const status = record?.status;
        if (status && statusCount.hasOwnProperty(status)) {
          statusCount[status]++;
        }
      });
    }
    return statusCount;
  };

  const statusSummary = getStatusSummary();

  // Loading skeleton
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Skeleton variant="text" width={300} height={40} />
          <Box display="flex" gap={2}>
            <Skeleton variant="rectangular" width={100} height={36} />
            <Skeleton variant="rectangular" width={120} height={36} />
          </Box>
        </Box>

        <Grid container spacing={3} mb={4}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>

        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 4 }} />

        {[1, 2].map((item) => (
          <Skeleton key={item} variant="rectangular" height={150} sx={{ borderRadius: 2, mb: 2 }} />
        ))}
      </Container>
    );
  }

  // Error state
  if (error && !loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Fee Records
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }

  // Empty state
  if (!Array.isArray(feeRecords) || feeRecords.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <ReceiptIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Fee Records Found
          </Typography>
          <Typography variant="body1" color="textSecondary">
            No fee records available
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size={isMobile ? 'small' : 'medium'}
            sx={{ mt: 2 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => window.history.back()}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary">
              Fee Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {feeRecords?.length || 0} record(s) found
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size={isMobile ? 'small' : 'medium'}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            size={isMobile ? 'small' : 'medium'}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportReport}
            size={isMobile ? 'small' : 'medium'}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Overall Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CurrencyRupee color="primary" />
                <Typography color="textSecondary" variant="body2">
                  Total Annual Fees
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {formatCurrency(totalAnnualFees)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {1 || 0} fee record(s)
              </Typography>
            </CardContent>
          </InfoCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CurrencyRupee color="primary" />
                <Typography color="textSecondary" variant="body2">
                  Total School Fees
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {formatCurrency(totalSchoolFees)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {1 || 0} fee record(s)
              </Typography>
            </CardContent>
          </InfoCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CurrencyRupee color="primary" />
                <Typography color="textSecondary" variant="body2">
                  Total Vehicle Fees
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="600">
                {formatCurrency(totalVehicleFees)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {1 || 0} fee record(s)
              </Typography>
            </CardContent>
          </InfoCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircleIcon color="success" />
                <Typography color="textSecondary" variant="body2">
                  Total Paid
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="600" color="success.main">
                {formatCurrency(totalPaid)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {overallProgress.toFixed(1)}% of total
              </Typography>
            </CardContent>
          </InfoCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PendingIcon color="warning" />
                <Typography color="textSecondary" variant="body2">
                  Remaining Balance
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="600" color="warning.main">
                {formatCurrency(totalRemaining)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Due for payment
              </Typography>
            </CardContent>
          </InfoCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <InfoCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssessmentIcon color="info" />
                <Typography color="textSecondary" variant="body2">
                  Payment Status
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                {Object.entries(statusSummary).map(([status, count]) => (
                  count > 0 && (
                    <StatusChip
                      key={status}
                      label={`${status} (${count})`}
                      status={status}
                      size="small"
                      icon={getStatusIcon(status)}
                    />
                  )
                ))}
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Overall Progress */}
      <StyledPaper>
        <Typography variant="h6" gutterBottom fontWeight="600">
          Overall Payment Progress
        </Typography>
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="textSecondary">
              {overallProgress.toFixed(1)}% Completed
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formatCurrency(totalPaid)} / {formatCurrency(totalAnnualFees)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(overallProgress, 100)}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                background: overallProgress === 100
                  ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                  : overallProgress > 50
                    ? 'linear-gradient(90deg, #2196f3, #4caf50)'
                    : 'linear-gradient(90deg, #ff9800, #f44336)'
              }
            }}
          />
        </Box>
      </StyledPaper>

      {/* Fee Records Grouped by Type */}
      <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mt: 4, mb: 2 }}>
        Fee Records by Type
      </Typography>

      {Object.keys(groupedFees).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary">No fee records found</Typography>
        </Paper>
      ) : (
        Object.entries(groupedFees).map(([feeType, group]) => (
          <Accordion
            key={feeType}
            expanded={expandedGroups[feeType] !== false}
            onChange={() => toggleGroup(feeType)}
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#fafafa',
                borderRadius: 2,
                '&.Mui-expanded': {
                  borderRadius: '12px 12px 0 0',
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={2} width="100%" flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  {getFeeTypeIcon(feeType)}
                  <Typography fontWeight="600" textTransform="capitalize">
                    {feeType}
                  </Typography>
                </Box>
                <StatusChip
                  label={group.status || 'N/A'}
                  status={group.status}
                  icon={getStatusIcon(group.status)}
                  size="small"
                />
                <Typography variant="body2" color="textSecondary">
                  {group.items.length} item(s)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total: {formatCurrency(group.totalAmount)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Paid: {formatCurrency(group.totalPaid)}
                </Typography>
                <Box flex={1} />
                <Typography variant="body2" color="textSecondary">
                  Progress: {group.totalAmount > 0 ? ((group.totalPaid / group.totalAmount) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><Typography fontWeight="600">#</Typography></TableCell>
                      <TableCell><Typography fontWeight="600">Fee Type</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight="600">Amount</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight="600">Paid</Typography></TableCell>
                      <TableCell align="center"><Typography fontWeight="600">Status</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight="600">Due Date</Typography></TableCell>
                      <TableCell align="center"><Typography fontWeight="600">Actions</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.items.map((fee, index) => (
                      <TableRow key={fee?.id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getFeeTypeIcon(fee?.feeType)}
                            <Typography textTransform="capitalize">
                              {fee?.feeType || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(fee?.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(fee?.feesEntry?.paidAmount)}
                        </TableCell>
                        <TableCell align="center">
                          <StatusChip
                            label={fee?.feesEntry?.status || 'N/A'}
                            status={fee?.feesEntry?.status}
                            size="small"
                            icon={getStatusIcon(fee?.feesEntry?.status)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {formatDate(fee?.feesEntry?.dueDate)}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadReceipt(fee)}
                              title="Download Receipt"
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDownloadReceipt(fee)}
                              title="Download Statement"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Summary Row */}
                    <TableRow sx={{ backgroundColor: '#fafafa' }}>
                      <TableCell colSpan={2}>
                        <Typography fontWeight="600">Group Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="600">{formatCurrency(group.totalAmount)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="600" color="success.main">
                          {formatCurrency(group.totalPaid)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <StatusChip
                          label={group.status || 'N/A'}
                          status={group.status}
                          size="small"
                          icon={getStatusIcon(group.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="textSecondary">
                          {group.totalAmount > 0 ? ((group.totalPaid / group.totalAmount) * 100).toFixed(1) : 0}% paid
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Progress for this group */}
              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    Group Payment Progress
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {group.totalAmount > 0 ? ((group.totalPaid / group.totalAmount) * 100).toFixed(1) : 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={group.totalAmount > 0 ? Math.min((group.totalPaid / group.totalAmount) * 100, 100) : 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    mt: 0.5,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: group.status === 'Paid'
                        ? '#4caf50'
                        : group.status === 'Partial'
                          ? '#ff9800'
                          : '#f44336'
                    }
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Summary Footer */}
      <StyledPaper sx={{ backgroundColor: '#f8f9fa' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Payment Summary
            </Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Total Records
                </Typography>
                <Typography fontWeight="600">{feeRecords?.length || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Total Amount
                </Typography>
                <Typography fontWeight="600">{formatCurrency(totalAnnualFees)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Total Paid
                </Typography>
                <Typography fontWeight="600" color="success.main">{formatCurrency(totalPaid)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">
                  Balance Due
                </Typography>
                <Typography fontWeight="600" color="warning.main">{formatCurrency(totalRemaining)}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Status Distribution
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {Object.entries(statusSummary).map(([status, count]) => (
                count > 0 && (
                  <Box key={status} display="flex" alignItems="center" gap={1}>
                    <StatusChip
                      label={`${count}`}
                      status={status}
                      size="small"
                    />
                    <Typography variant="body2" color="textSecondary">
                      {status}
                    </Typography>
                  </Box>
                )
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="textSecondary" align="center">
          <strong>Note:</strong> This is a computer-generated report. For any queries regarding fee payments,
          please contact the accounts department. Last updated: {formatDateTime(new Date().toISOString())}
        </Typography>
      </StyledPaper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FeeView;